/**
 * Server-side logger for the Breuer Klimaattechniek website.
 *
 * Collects log entries in an in-memory buffer and flushes them
 * to the API via POST /logs/batch in a fire-and-forget fashion.
 *
 * - Errors flush immediately
 * - Warnings flush within 2 seconds
 * - Info/debug batch up to 5 seconds or 20 entries
 * - Never blocks the calling code
 * - Falls back to console if the API is unreachable
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
	app: string;
	level: LogLevel;
	message: string;
	context?: string;
	request_id?: string;
}

// ── Configuration ───────────────────────────────────────────────

const APP_ID = 'website';
const API_URL = process.env.API_URL || 'http://127.0.0.1:8000';
const CLIENT_ID = process.env.API_CLIENT_ID;
const CLIENT_SECRET = process.env.API_CLIENT_SECRET;

const LEVEL_RANK: Record<LogLevel, number> = { debug: 0, info: 1, warn: 2, error: 3 };
const configuredLevel = (process.env.LOG_LEVEL as LogLevel | undefined) ?? (process.env.NODE_ENV === 'production' ? 'info' : 'debug');
const MIN_LEVEL = LEVEL_RANK[configuredLevel] ?? LEVEL_RANK['info'];

const BATCH_SIZE = 20;
const FLUSH_INTERVAL_MS = 5_000;
const WARN_FLUSH_MS = 2_000;

// ── Token cache (shared with api/client.ts module scope) ────────

let cachedToken: string | null = null;
let tokenExpiresAt = 0;

async function getApiToken(): Promise<string> {
	const now = Date.now();
	if (cachedToken && now < tokenExpiresAt) {
		return cachedToken;
	}

	if (!CLIENT_ID || !CLIENT_SECRET) {
		throw new Error('API_CLIENT_ID / API_CLIENT_SECRET not configured');
	}

	const res = await fetch(`${API_URL}/auth/token`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({
			client_id: CLIENT_ID,
			client_secret: CLIENT_SECRET,
		}),
	});

	if (!res.ok) {
		throw new Error(`API auth failed: ${res.status}`);
	}

	const data = await res.json();
	cachedToken = data.access_token;
	tokenExpiresAt = now + 10 * 60 * 1000;

	return cachedToken!;
}

// ── Buffer & flush logic ────────────────────────────────────────

let buffer: LogEntry[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let warnTimer: ReturnType<typeof setTimeout> | null = null;
let isFlushing = false;

// Circuit breaker: stop hitting the API after consecutive failures
let consecutiveFailures = 0;
let disabledUntil = 0;
const MAX_FAILURES = 3;
const BACKOFF_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Send buffered entries to the API. Fire-and-forget — errors are
 * swallowed and logged to the native console as a last resort.
 */
async function flush(): Promise<void> {
	if (buffer.length === 0 || isFlushing) return;

	// Circuit breaker: skip if we've failed too many times recently
	const now = Date.now();
	if (consecutiveFailures >= MAX_FAILURES && now < disabledUntil) {
		return;
	}

	const entries = buffer.splice(0); // drain buffer
	isFlushing = true;

	try {
		const token = await getApiToken();

		const res = await fetch(`${API_URL}/logs/batch`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${token}`,
			},
			body: JSON.stringify({ entries }),
		});

		if (!res.ok) {
			buffer.unshift(...entries);
			consecutiveFailures++;
		} else {
			consecutiveFailures = 0; // Reset on success
		}
	} catch (err) {
		// API unreachable — drop entries (don't re-queue endlessly)
		consecutiveFailures++;
		if (consecutiveFailures === MAX_FAILURES) {
			disabledUntil = Date.now() + BACKOFF_MS;
			console.warn(`[logger] API unreachable after ${MAX_FAILURES} attempts, disabled for 5min`);
			buffer = []; // Drop queued entries to prevent memory leak
		}
	} finally {
		isFlushing = false;
	}
}

function scheduleFlush(): void {
	if (flushTimer) return;
	flushTimer = setTimeout(() => {
		flushTimer = null;
		void flush();
	}, FLUSH_INTERVAL_MS);
}

function scheduleWarnFlush(): void {
	if (warnTimer) return;
	warnTimer = setTimeout(() => {
		warnTimer = null;
		void flush();
	}, WARN_FLUSH_MS);
}

function cancelTimers(): void {
	if (flushTimer) {
		clearTimeout(flushTimer);
		flushTimer = null;
	}
	if (warnTimer) {
		clearTimeout(warnTimer);
		warnTimer = null;
	}
}

// ── Public API ──────────────────────────────────────────────────

function log(level: LogLevel, message: string, context?: Record<string, unknown>): void {
	if (LEVEL_RANK[level] < MIN_LEVEL) return;

	const consoleFn = level === 'error' ? console.error : level === 'warn' ? console.warn : console.log;
	consoleFn(`[${level.toUpperCase()}]`, message, context ?? '');

	const requestId = typeof context?.request_id === 'string' ? context.request_id : undefined;

	const entry: LogEntry = {
		app: APP_ID,
		level,
		message,
		context: context ? JSON.stringify(context) : undefined,
		request_id: requestId,
	};

	buffer.push(entry);

	// Priority-based flush
	if (level === 'error') {
		cancelTimers();
		void flush();
	} else if (level === 'warn') {
		scheduleWarnFlush();
	} else if (buffer.length >= BATCH_SIZE) {
		cancelTimers();
		void flush();
	} else {
		scheduleFlush();
	}
}

export const logger = {
	debug: (message: string, context?: Record<string, unknown>) => log('debug', message, context),
	info: (message: string, context?: Record<string, unknown>) => log('info', message, context),
	warn: (message: string, context?: Record<string, unknown>) => log('warn', message, context),
	error: (message: string, context?: Record<string, unknown>) => log('error', message, context),
	/**
	 * Force-flush remaining entries. Call in shutdown hooks
	 * or at the end of a request cycle if needed.
	 */
	flush,
};

// ── Graceful shutdown ───────────────────────────────────────────

if (typeof process !== 'undefined') {
	const shutdownFlush = () => {
		cancelTimers();
		if (buffer.length > 0) {
			// Synchronous-ish best effort: we fire the flush and hope the
			// runtime gives us enough time to complete.
			void flush();
		}
	};

	process.on('beforeExit', shutdownFlush);
	process.on('SIGTERM', shutdownFlush);
	process.on('SIGINT', shutdownFlush);
}
